import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withTheme } from 'common/styling/theme';
import { Link } from 'react-router-dom';
import { isAgreementAutoAcknowledgedBroker } from 'common/utils';
// import { DefaultButton } from 'common/components';
// 导入获取账户列表和账户类型的action
import { getTradingAccountList, getTradingAccountTypes } from 'client/_redux/actions';
import { FormattedMessage } from 'react-intl';
import { staticStyles } from './style';
import AccountsBlock from '../../../_components/AccountsBlock';
import { areAllDocumentsValid } from '../../_utils/documentUtils';
import { getChangePasswordStatusFromRedis, getKYCStatusFromRedis } from '../../api';

const PersonalPage = ({
  userInfo,
  demoAccounts = [],
  history,
  accountTypes,
  documents,
  kycSettings,
  interfaceConfig,
}) => {
  const dispatch = useDispatch();
  // 尝试从不同位置获取用户名字
  const firstName = userInfo?.firstName || userInfo?.first_name || '';
  const lastName = userInfo?.lastName || userInfo?.last_name || '';

  // 组合名字，如果都没有则fallback到User
  const fullName = `${firstName} ${lastName}`.trim() || 'User';

  // 判断所有必要文档是否有效（待审核或审核通过）
  const documentsValid = areAllDocumentsValid(documents, kycSettings);

  // 存储KYC表单状态
  const [kycFormStatus, setKycFormStatus] = useState('not_saved');

  // 存储changePassword表单状态
  const [changePasswordForm, setChangePasswordFormStatus] = useState('not_saved');

  // 存储弹窗显示状态
  const [showModal, setShowModal] = useState(false);

  // 指定 broker（如 10/229）无需签署反向招揽文档，不展示相关弹窗
  const agreementAutoAcknowledged = isAgreementAutoAcknowledgedBroker(interfaceConfig?.brokerId);

  // 组件挂载时获取账户列表、账户类型以及KYC表单状态
  useEffect(() => {
    // 调用获取账户列表的action
    dispatch(getTradingAccountList.request());
    // 调用获取账户类型的action
    dispatch(getTradingAccountTypes.request());

    // 获取KYC表单状态
    const fetchKycStatus = async () => {
      try {
        const response = await getKYCStatusFromRedis();
        // 确保status字段存在
        const status = response.data?.status || 'not_saved';
        setKycFormStatus(status);
      } catch (error) {
        console.error('Failed to fetch KYC form status:', error); // eslint-disable-line no-console
        // 出错时保持默认状态为not_saved
      }
    };

    // 获取KYC表单状态
    const fetchChangePasswordStatus = async () => {
      try {
        const response = await getChangePasswordStatusFromRedis();
        // 确保status字段存在
        const status = response.data?.status || 'not_saved';
        setChangePasswordFormStatus(status);
      } catch (error) {
        console.error('Failed to fetch form status:', error); // eslint-disable-line no-console
        // 出错时保持默认状态为not_saved
      }
    };

    fetchKycStatus();
    fetchChangePasswordStatus();
  }, [dispatch]);

  // 页面加载时自动检查是否需要显示弹窗
  useEffect(() => {
    // 确保userInfo和interfaceConfig已加载
    if (userInfo && interfaceConfig && !agreementAutoAcknowledged) {
      const userStatus = userInfo?.status || '';
      const isSolicitationAcknowledge = userInfo?.isSolicitationAcknowledge || false;
      const isAgreementAcknowledged = userInfo?.isAgreementAcknowledged || false;
      const userCountry = userInfo?.nationality || '';
      // 假设WhiteListCountriesSetting在interfaceConfig.other中，根据后端实现检查配置
      const whiteListCountriesSetting = interfaceConfig?.whiteListCountriesSetting || {};
      const isWhiteListEnabled = whiteListCountriesSetting.enabled;
      const whiteListCountries = whiteListCountriesSetting.countries || [];
      const isUserInWhiteList = whiteListCountries.includes(userCountry);

      // 检查是否需要显示弹窗的条件：
      // 1. 用户未确认过客户协议
      // 2. 用户状态不是verified时，如果用户未确认过反向招揽协议 且 用户不在白名单国家中（或白名单功能未启用）
      if (
        !isAgreementAcknowledged ||
        (userStatus !== 'verified' && !(isWhiteListEnabled && isUserInWhiteList) && !isSolicitationAcknowledge)
      ) {
        // 延迟一小段时间显示弹窗，确保页面完全加载
        setTimeout(() => {
          setShowModal(true);
        }, 500);
      }
    }
  }, [userInfo, interfaceConfig, agreementAutoAcknowledged]);

  // 处理弹窗拒绝操作
  const handleDecline = () => {
    // 这里将添加拒绝操作的逻辑
    dispatch({
      type: 'SHOW_NOTIFICATION',
      payload: {
        type: 'error',
        message: 'Action Required',
        description: 'You must acknowledge the Reverse Solicitation document to continue.',
      },
    });
    setShowModal(false);
  };

  // 步骤数据
  const steps = [
    {
      id: 1,
      name: <FormattedMessage id="verifyPersonalProfile" />,
      // 这里可以添加实际的跳转链接
      link: '/personal_profile',
      status: kycFormStatus,
    },
    {
      id: 2,
      name: <FormattedMessage id="uploadDocuments" />,
      // 这里可以添加实际的跳转链接
      link: '/documents',
      // 文档已上传且有效（待审核或审核通过）
      status: documentsValid,
    },
    {
      id: 3,
      name: <FormattedMessage id="setUpSecurity" />,
      // 这里可以添加实际的跳转链接
      link: '/change_password',
      status: changePasswordForm,
    },
  ];

  const showDemoAccounts =
    Array.isArray(accountTypes) && accountTypes.some(account => account.isDemo && !account.isBrokeree);

  return (
    <div className="personal-page-container">
      <span className="user-name">Hello {fullName}</span>
      <div className="verification-prompt">
        <span className="verification-prompt-span">
          <FormattedMessage
            id="personalPageTips"
            defaultProps="Secure your account. Complete verification to start trading. Meanwhile, try our risk-free demo."
          />
        </span>
      </div>

      {/* 进度条和标题作为整体 */}
      <div className="verification-container">
        {/* 进度条标题 */}
        <h3 className="verification-title">
          <FormattedMessage id="verificationCheck" />
        </h3>

        {/* 进度条和步骤名称作为一个整体 */}
        <div className="verification-steps-wrapper">
          {/* 进度条组件 */}
          <div className="verification-progress">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                {/* 步骤数字 */}
                <div className="step-number-container">
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <div
                    className={`step-number ${step.status === 'temporary_saved' ? 'step-number-processing' : ''} ${
                      step.status === 'submitted' ? 'step-number-completed' : ''
                    }`}
                    onClick={e => {
                      // 当用户点击步骤1或2时，检查协议确认状态
                      const userStatus = userInfo?.status || '';
                      const isSolicitationAcknowledge = userInfo?.isSolicitationAcknowledge || false;
                      const isAgreementAcknowledged = userInfo?.isAgreementAcknowledged || false;
                      const userCountry = userInfo?.nationality || '';
                      // 假设WhiteListCountriesSetting在interfaceConfig.other中，根据后端实现检查配置
                      const whiteListCountriesSetting = interfaceConfig?.whiteListCountriesSetting || {};
                      const isWhiteListEnabled = whiteListCountriesSetting.enabled;
                      const whiteListCountries = whiteListCountriesSetting.countries || [];
                      const isUserInWhiteList = whiteListCountries.includes(userCountry);

                      // 白名单功能开启且用户国家在白名单时不显示弹窗
                      // 白名单功能未开启 或 用户国家不在白名单中时，再判断用户是否确认
                      // 检查条件：
                      // 1. 用户未确认过客户协议
                      // 2. 用户状态不是verified时，如果用户未确认过反向招揽协议 且 用户不在白名单国家中（或白名单功能未启用）
                      if (
                        !agreementAutoAcknowledged &&
                        (step.id === 1 || step.id === 2) &&
                        (!isAgreementAcknowledged ||
                          (userStatus !== 'verified' &&
                            !(isWhiteListEnabled && isUserInWhiteList) &&
                            !isSolicitationAcknowledge))
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowModal(true);
                      } else {
                        history.push(step.link);
                      }
                    }}
                    tabIndex="0"
                    role="button"
                    aria-label={`Navigate to ${step.name}`}
                    style={{ cursor: 'pointer' }}>
                    {step.status === 'temporary_saved' || step.status === 'submitted' ? '✓' : step.id}
                  </div>
                  <Link
                    key={step.id}
                    to={step.link}
                    className={`step-name-link ${step.completed ? 'completed' : ''}`}
                    onClick={e => {
                      // 当用户点击步骤1或2的名称链接时，检查协议确认状态
                      const userStatus = userInfo?.status || '';
                      const isSolicitationAcknowledge = userInfo?.isSolicitationAcknowledge || false;
                      const isAgreementAcknowledged = userInfo?.isAgreementAcknowledged || false;
                      const userCountry = userInfo?.nationality || '';
                      // 假设WhiteListCountriesSetting在interfaceConfig.other中，根据后端实现检查配置
                      const whiteListCountriesSetting = interfaceConfig?.whiteListCountriesSetting || {};
                      const isWhiteListEnabled = whiteListCountriesSetting.enabled;
                      const whiteListCountries = whiteListCountriesSetting.countries || [];
                      const isUserInWhiteList = whiteListCountries.includes(userCountry);
                      // 白名单功能开启且用户国家在白名单时不显示弹窗
                      // 白名单功能未开启 或 用户国家不在白名单中时，再判断用户是否确认
                      // 检查条件：
                      // 1. 用户未确认过客户协议
                      // 2. 用户状态不是verified时，如果用户未确认过反向招揽协议 且 用户不在白名单国家中（或白名单功能未启用）
                      if (
                        !agreementAutoAcknowledged &&
                        (step.id === 1 || step.id === 2) &&
                        (!isAgreementAcknowledged ||
                          (userStatus !== 'verified' &&
                            !(isWhiteListEnabled && isUserInWhiteList) &&
                            !isSolicitationAcknowledge))
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowModal(true);
                      }
                    }}>
                    {step.name}
                  </Link>
                </div>

                {/* 连接线（除了最后一个步骤） */}
                {index < steps.length - 1 && <div className="step-connector" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* 虚拟账户展示模块 */}
      <div className="demo-accounts-container">
        <AccountsBlock
          data={demoAccounts}
          captionId="demoAccounts"
          buttonId="addDemoAccount"
          noAccCaptionId="noDemoAccsCreated"
          onAccountClick={id => history.push(`/account/${id}`)}
          onAddAccountClick={showDemoAccounts ? () => history.push('/create-account/demo') : null}
          isDemo
        />
      </div>

      {/* 弹窗组件 */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <button type="button" className="modal-close-btn" onClick={handleDecline} aria-label="Close modal">
              ✕
            </button>
            <h2 className="modal-title">Please Complete Required Document Signatures</h2>
            <p className="modal-subtitle">
              To comply with international financial regulations and secure your legitimate trading rights, please sign
              the required documents below for the requested services.
            </p>

            <div className="modal-buttons">
              <button
                type="button"
                className="modal-agree-btn"
                onClick={() => {
                  setShowModal(false);
                  history.push('/reverse-solicitation');
                }}>
                Sign Now
              </button>
              <button type="button" className="modal-decline-btn" onClick={handleDecline}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {`
          /* 弹窗样式 */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-container {
            background-color: #ffffff;
            width: 500px;
            height: 450px;
            border-radius: 16px;
            padding: 30px;
            box-shadow: 0px 6px 30px 1px rgba(0, 0, 0, 0.06);
            border: 1px solid rgba(44, 0, 143, 0.2);
            position: relative;
            display: flex;
            flex-direction: column;
          }

          .modal-close-btn {
            position: absolute;
            top: 30px;
            right: 30px;
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #292d32;
          }

          .modal-title {
            font-family: 'Samsung Sharp Sans', sans-serif;
            font-weight: 700;
            font-size: 22px;
            line-height: 1.545;
            color: #000000;
            text-align: center;
            margin-top: 58px;
            margin-bottom: 50px;
          }

          .modal-subtitle {
            font-family: 'Red Hat Display', sans-serif;
            font-weight: 600;
            font-size: 18px;
            line-height: 1.323;
            color: #000000;
            opacity: 0.6;
            text-align: center;
            margin-bottom: 50px;
          }

          .modal-document {
            flex: 1;
            background-color: #f5f5f5;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: auto;
          }

          .modal-buttons {
            display: flex;
            justify-content: space-between;
          }

          .modal-agree-btn {
            width: 200px;
            height: 60px;
            background-color: #4500e2;
            color: #ffffff;
            border: none;
            border-radius: 14px;
            font-family: 'Samsung Sharp Sans', sans-serif;
            font-weight: 700;
            font-size: 14px;
            line-height: 1.258;
            cursor: pointer;
          }

          .modal-agree-btn:hover {
            background-color: #3600b8;
          }

          .modal-decline-btn {
            width: 200px;
            height: 60px;
            background-color: #ffffff;
            color: #2c008f;
            border: 1px solid #2c008f;
            border-radius: 14px;
            font-family: 'Samsung Sharp Sans', sans-serif;
            font-weight: 700;
            font-size: 14px;
            line-height: 1.258;
            cursor: pointer;
          }

          .modal-decline-btn:hover {
            background-color: #f5f5f5;
          }

          .view-full-document-btn {
            width: 100%;
            padding: 10px;
            margin-top: 15px;
            background-color: transparent;
            color: #2c008f;
            border: 1px solid #2c008f;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
          }

          .view-full-document-btn:hover {
            background-color: #f5f5f5;
          }
        `}
      </style>
    </div>
  );
};

PersonalPage.propTypes = {
  userInfo: PropTypes.object,
  demoAccounts: PropTypes.array,
  history: PropTypes.object.isRequired,
  accountTypes: PropTypes.array.isRequired,
  // eslint-disable-next-line react/require-default-props
  documents: PropTypes.object.isRequired,
  kycSettings: PropTypes.object.isRequired,
  interfaceConfig: PropTypes.object.isRequired, // 用于获取broker配置
};

PersonalPage.defaultProps = {
  userInfo: {},
  demoAccounts: [],
  // eslint-disable-next-line react/default-props-match-prop-types
  documents: {},
};

export default compose(
  withTheme(),
  connect(state => ({
    // 尝试从多个可能的位置获取用户信息
    userInfo: state.user.profile || {},
    // 文档状态数据，用于判断文档是否已全部上传
    // documents: state.user.profile || {},
    // 修复：从正确的路径 state.client.common.accounts 获取用户的虚拟账户
    demoAccounts: Array.isArray(state.client?.common?.accounts)
      ? state.client.common.accounts.filter(account => account.isDemo)
      : [],
    accountTypes: state.client.common.accountTypes,
    documents: {
      passportStatus: state.user.profile?.passportStatus,
      passportWithFaceStatus: state.user.profile?.passportWithFaceStatus,
      addressProofStatus: state.user.profile?.addressProofStatus,
      identificationProofSummary: state.user.profile?.identificationProofSummary,
      additionalDocumentStatus: state.user.profile?.additionalDocumentStatus,
    },
    kycSettings: state.interfaceConfig.kycSettings,
    interfaceConfig: state.interfaceConfig,
  }))
)(PersonalPage);
export { PersonalPage };
